/**
 * Content library.
 *
 * A curated content system, not a gallery. The previous version rendered a full
 * lock-screen mockup on every card, which meant a library of forty templates was
 * forty phone screens deep and impossible to scan - the exact opposite of what a
 * library is for.
 *
 * Rows instead: identity on the left, copy in the middle, provenance on the
 * right, actions at the end. The preview is one click away rather than always
 * on, because an operator browsing for the right line reads the LINE, and only
 * wants to see it rendered once they have a candidate.
 *
 * Sorted by usage: the point of a library is to make the copy that already
 * worked the easiest thing to reach for.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Copy, Eye, Pencil, Plus, Sparkles } from 'lucide-react'
import { useColors } from '../../hooks/useColors'
import {
  Chip, Field, Modal, PageHeader, PillButton,
  SearchInput, SelectInput, TextArea, TextInput,
} from '../../components/shared/primitives'
import NotificationPreview from '../../features/notifications/components/NotificationPreview'
import { notificationsApi } from '../../features/notifications/api'
import {
  CAMPAIGN_KINDS, CTA_OPTIONS, DEEPLINK_OPTIONS, iconForCategory,
} from '../../features/notifications/constants'
import type { Template } from '../../features/notifications/types'
import '../../styles/notifications.css'

const ALL_CATEGORIES = Array.from(new Set(CAMPAIGN_KINDS.flatMap((kind) => kind.categories))).sort()

const emptyDraft = {
  name: '', category: 'OFFER', title: '', body: '', cta_label: '', deeplink: '', tags: '',
}

/** "3 days ago" beats a date here - recency is the only thing being judged. */
const relativeDate = (value: string | null) => {
  if (!value) return 'never'
  const then = new Date(value).getTime()
  if (Number.isNaN(then)) return 'unknown'
  const days = Math.floor((Date.now() - then) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  const months = Math.floor(days / 30)
  return months === 1 ? 'a month ago' : `${months} months ago`
}

export default function TemplateLibraryPage() {
  const colors = useColors()
  const navigate = useNavigate()

  const [templates, setTemplates] = useState<Template[]>([])
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [error, setError] = useState('')

  /** One modal serves create and edit; `editing` decides which. */
  const [composerOpen, setComposerOpen] = useState(false)
  const [editing, setEditing] = useState<Template | null>(null)
  const [draft, setDraft] = useState(emptyDraft)
  const [saving, setSaving] = useState(false)
  const [previewing, setPreviewing] = useState<Template | null>(null)

  const load = useCallback(async () => {
    try {
      setTemplates(await notificationsApi.listTemplates({ category: category || undefined }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the library')
    }
  }, [category])

  useEffect(() => {
    void load()
  }, [load])

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return templates
    return templates.filter((template) =>
      `${template.name} ${template.title} ${template.body} ${(template.tags || []).join(' ')}`
        .toLowerCase()
        .includes(needle)
    )
  }, [templates, search])

  const openCreate = () => {
    setEditing(null)
    setDraft(emptyDraft)
    setComposerOpen(true)
  }

  const openEdit = (template: Template) => {
    setEditing(template)
    setDraft({
      name: template.name,
      category: template.category,
      title: template.title,
      body: template.body,
      cta_label: template.cta_label ?? '',
      deeplink: template.deeplink ?? '',
      tags: (template.tags || []).join(', '),
    })
    setComposerOpen(true)
  }

  const save = async () => {
    setError('')
    setSaving(true)
    try {
      const payload = {
        ...draft,
        tags: draft.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      } as unknown as Partial<Template>
      if (editing) await notificationsApi.updateTemplate(editing.id, payload)
      else await notificationsApi.createTemplate(payload)
      setComposerOpen(false)
      setEditing(null)
      setDraft(emptyDraft)
      await load()
    } catch (err) {
      const response = (err as { response?: { data?: { message?: string } } }).response
      setError(response?.data?.message || 'Could not save the template')
    } finally {
      setSaving(false)
    }
  }

  /**
   * Duplicate is a create with the source's fields - there is no duplicate
   * endpoint, and inventing one for a client-side copy would be a backend change
   * this redesign does not need. The copy lands as a fresh manual template.
   */
  const duplicate = async (template: Template) => {
    setError('')
    try {
      await notificationsApi.createTemplate({
        name: `${template.name} (copy)`,
        category: template.category,
        title: template.title,
        body: template.body,
        image_url: template.image_url,
        cta_label: template.cta_label,
        deeplink: template.deeplink,
        tags: template.tags || [],
      } as unknown as Partial<Template>)
      await load()
    } catch (err) {
      const response = (err as { response?: { data?: { message?: string } } }).response
      setError(response?.data?.message || 'Could not duplicate the template')
    }
  }

  return (
    <div>
      <PageHeader
        title="Content library"
        subtitle="Reusable Rapidry copy, ordered by what has actually earned its place."
        actions={(
          <>
            <PillButton variant="ghost" onClick={() => navigate('/notifications')}>
              <ArrowLeft size={14} /> Notification Center
            </PillButton>
            <PillButton onClick={openCreate}>
              <Plus size={14} /> New template
            </PillButton>
          </>
        )}
      />

      <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search copy or tags" width="260px" />
        <SelectInput
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          style={{ width: '210px', padding: '8px 10px' }}
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {ALL_CATEGORIES.map((value) => <option key={value} value={value}>{value}</option>)}
        </SelectInput>
        <span style={{ marginLeft: 'auto' }} className="ntf-eyebrow">
          {visible.length} {visible.length === 1 ? 'template' : 'templates'}
        </span>
      </div>

      {error ? (
        <div
          style={{
            border: '1px solid #B45309', borderRadius: '10px', padding: '12px 14px',
            marginBottom: '16px', color: '#B45309', fontSize: '13px',
          }}
        >
          {error}
        </div>
      ) : null}

      <div className="ntf-library">
        {visible.map((template) => {
          const Icon = iconForCategory(template.category)
          return (
            <div key={template.id} className="ntf-tpl">
              <span className="ntf-tpl__icon" aria-hidden>
                <Icon size={15} strokeWidth={1.75} />
              </span>

              <div style={{ minWidth: 0 }}>
                <div className="ntf-tpl__name">
                  {template.name}
                  {template.source === 'ai' ? (
                    <Chip tone="info" style={{ marginLeft: 8 }}>
                      <Sparkles size={11} /> AI draft
                    </Chip>
                  ) : null}
                </div>
                <p className="ntf-tpl__copy">
                  <span style={{ color: colors.textSecondary, fontWeight: 500 }}>{template.title}</span>
                  {' · '}
                  {template.body}
                </p>
              </div>

              <div className="ntf-tpl__meta">
                <span style={{ letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '10px', fontWeight: 700 }}>
                  {template.category}
                </span>
                <span>
                  {template.usage_count > 0
                    ? `Used ${template.usage_count}× · last ${relativeDate(template.last_used_at)}`
                    : 'Never used'}
                </span>
                <span>Updated {relativeDate(template.updated_at)}</span>
              </div>

              <div className="ntf-tpl__actions">
                <button
                  type="button"
                  className="ntf-iconbtn"
                  title="Preview"
                  aria-label={`Preview ${template.name}`}
                  onClick={() => setPreviewing(template)}
                >
                  <Eye size={14} />
                </button>
                <button
                  type="button"
                  className="ntf-iconbtn"
                  title="Edit"
                  aria-label={`Edit ${template.name}`}
                  onClick={() => openEdit(template)}
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  className="ntf-iconbtn"
                  title="Duplicate"
                  aria-label={`Duplicate ${template.name}`}
                  onClick={() => void duplicate(template)}
                >
                  <Copy size={14} />
                </button>
                <PillButton
                  variant="secondary"
                  style={{ padding: '7px 14px' }}
                  onClick={() => navigate(`/notifications/new?template=${template.id}`)}
                >
                  Use
                </PillButton>
              </div>
            </div>
          )
        })}

        {visible.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.textMuted, fontSize: '13px' }}>
            Nothing matches that search.
          </div>
        ) : null}
      </div>

      {previewing ? (
        <Modal
          title={previewing.name}
          width="380px"
          onClose={() => setPreviewing(null)}
          footer={(
            <>
              <PillButton variant="secondary" onClick={() => setPreviewing(null)}>Close</PillButton>
              <PillButton onClick={() => navigate(`/notifications/new?template=${previewing.id}`)}>
                Use this
              </PillButton>
            </>
          )}
        >
          <div style={{ display: 'grid', gap: '12px' }}>
            <NotificationPreview
              platform="android"
              title={previewing.title}
              body={previewing.body}
              ctaLabel={previewing.cta_label}
              imageUrl={previewing.image_url}
            />
            <NotificationPreview
              platform="ios"
              title={previewing.title}
              body={previewing.body}
              ctaLabel={previewing.cta_label}
              imageUrl={previewing.image_url}
            />
            {(previewing.tags || []).length > 0 ? (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {previewing.tags.map((tag) => <Chip key={tag}>{tag}</Chip>)}
              </div>
            ) : null}
          </div>
        </Modal>
      ) : null}

      {composerOpen ? (
        <Modal
          title={editing ? 'Edit template' : 'New template'}
          width="540px"
          onClose={() => setComposerOpen(false)}
          footer={(
            <>
              <PillButton variant="secondary" onClick={() => setComposerOpen(false)}>Cancel</PillButton>
              <PillButton
                onClick={() => void save()}
                disabled={saving || !draft.title.trim() || !draft.body.trim() || !draft.name.trim()}
              >
                {editing ? 'Save changes' : 'Save to library'}
              </PillButton>
            </>
          )}
        >
          <div style={{ display: 'grid', gap: '12px' }}>
            <Field label="Name" hint="Internal. How you will find it again.">
              <TextInput value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
            </Field>
            <Field label="Category">
              <SelectInput value={draft.category} onChange={(event) => setDraft({ ...draft, category: event.target.value })}>
                {ALL_CATEGORIES.map((value) => <option key={value} value={value}>{value}</option>)}
              </SelectInput>
            </Field>
            <Field label="Title">
              <TextInput value={draft.title} onChange={(event) => setDraft({ ...draft, title: event.target.value })} maxLength={150} />
            </Field>
            <Field label="Body">
              <TextArea rows={3} value={draft.body} onChange={(event) => setDraft({ ...draft, body: event.target.value })} />
            </Field>
            <Field label="Call to action">
              <SelectInput value={draft.cta_label} onChange={(event) => setDraft({ ...draft, cta_label: event.target.value })}>
                <option value="">None</option>
                {CTA_OPTIONS.map((value) => <option key={value} value={value}>{value}</option>)}
              </SelectInput>
            </Field>
            <Field label="Opens">
              <SelectInput value={draft.deeplink} onChange={(event) => setDraft({ ...draft, deeplink: event.target.value })}>
                {DEEPLINK_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </SelectInput>
            </Field>
            <Field label="Tags" hint="Comma separated. Weekend, monsoon, college…">
              <TextInput value={draft.tags} onChange={(event) => setDraft({ ...draft, tags: event.target.value })} />
            </Field>

            <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '14px' }}>
              <div className="ntf-eyebrow" style={{ marginBottom: '8px' }}>Live preview</div>
              <NotificationPreview
                platform="android"
                title={draft.title}
                body={draft.body}
                ctaLabel={draft.cta_label}
              />
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}
